import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createJob, getEffectiveApiKey } from '../../services/syncApi';
import { DEFAULT_ENVIRONMENT, ENVIRONMENTS, ENVIRONMENT_LABELS } from '../../constants/markets';

const ALL_MARKETS = ['AU', 'NZ'];
const ALL_STAGES = ['create', 'update', 'assign_channel', 'assign_category'];

// Default stage set excludes `create` because it POSTs new BC products with
// weight=0/price=0/is_visible=false defaults, which is almost never what
// somebody clicking a top-level "Sync All Markets" button wants.
const DEFAULT_STAGES = ['update', 'assign_channel', 'assign_category'];

/**
 * One-click full-catalog sync. Sends `skus: null` so the worker pulls every
 * eligible row from `bigcommerce_sync_view`. One Postgres job per market;
 * markets run in parallel because they hold separate advisory locks.
 *
 * Safety rails:
 *  - Dry-run is on by default.
 *  - `create` is off by default and shows an inline warning when toggled on.
 *  - Turning off dry-run reveals a destructive-action confirmation.
 *  - Disabled when no API key is set; uses the same auth surface as Manual Sync.
 */
const SyncAllMarketsCard = () => {
  const [environment, setEnvironment] = useState(DEFAULT_ENVIRONMENT);
  const [markets, setMarkets] = useState(['AU', 'NZ']);
  const [mode, setMode] = useState('mutate');
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [dryRun, setDryRun] = useState(true);
  const [forceFresh, setForceFresh] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // results: [{ market, jobId?, error?, status: 'pending'|'success'|'error' }]
  const [results, setResults] = useState([]);

  const apiKeyPresent = Boolean(getEffectiveApiKey());

  const toggleMarket = (m) => {
    setMarkets((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  };
  const toggleStage = (s) => {
    setStages((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const validationError = useMemo(() => {
    if (!apiKeyPresent) return 'API key required — set it in the sidebar Credentials box.';
    if (markets.length === 0) return 'Select at least one market.';
    if (mode === 'mutate' && stages.length === 0) return 'Mutate mode requires at least one stage.';
    return null;
  }, [apiKeyPresent, markets.length, mode, stages.length]);

  const onSubmit = async () => {
    // For real (non-dry) mutate runs, surface a hard confirm: this is the
    // "sync EVERYTHING" button, hitting both markets by default.
    if (mode === 'mutate' && !dryRun) {
      const stageStr = stages.join(', ');
      const marketStr = markets.join(' + ');
      const confirmed = window.confirm(
        `This will push real changes to the ${environment.toUpperCase()} BigCommerce store(s) for ALL products in ${marketStr}.\n\n` +
        `Stages: ${stageStr}\n` +
        `Dry-run: OFF\n\n` +
        `Continue?`,
      );
      if (!confirmed) return;
    }

    setSubmitting(true);
    setResults(markets.map((m) => ({ market: m, status: 'pending' })));

    const payloadBase = {
      environment,
      mode,
      stage_filter: mode === 'mutate' ? stages : [],
      skus: null,
      limit: null,
      dry_run: dryRun,
      force_fresh: forceFresh,
    };

    const settled = await Promise.allSettled(
      markets.map((market) =>
        createJob({ ...payloadBase, store_code: market }).then(
          (data) => ({ market, status: 'success', jobId: data.job_id }),
          (err) => ({
            market,
            status: 'error',
            error: err?.response?.data?.detail || err.message || 'unknown',
          }),
        ),
      ),
    );

    setResults(
      settled.map((s, i) =>
        s.status === 'fulfilled'
          ? s.value
          : { market: markets[i], status: 'error', error: 'promise rejected' },
      ),
    );
    setSubmitting(false);
  };

  const showCreateWarning = mode === 'mutate' && stages.includes('create');
  const showRealMutateWarning = mode === 'mutate' && !dryRun;

  return (
    <section className="card sync-all-card">
      <header className="sync-all-header">
        <h2>Sync All Markets</h2>
        <span className="muted">Run compare or mutate against every eligible product in each selected market.</span>
      </header>

      <div className="sync-all-grid">
        <div className="form-group">
          <label className="form-label">Environment</label>
          <div className="checkbox-row">
            {ENVIRONMENTS.map((env) => (
              <label key={env} className="chip">
                <input
                  type="radio"
                  name="sync-all-environment"
                  checked={environment === env}
                  onChange={() => setEnvironment(env)}
                />
                {ENVIRONMENT_LABELS[env] || env}
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Markets</label>
          <div className="checkbox-row">
            {ALL_MARKETS.map((m) => (
              <label key={m} className="chip">
                <input type="checkbox" checked={markets.includes(m)} onChange={() => toggleMarket(m)} />
                {m}
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Mode</label>
          <div className="checkbox-row">
            <label className="chip">
              <input type="radio" name="sync-all-mode" checked={mode === 'compare'} onChange={() => setMode('compare')} />
              Compare
            </label>
            <label className="chip">
              <input type="radio" name="sync-all-mode" checked={mode === 'mutate'} onChange={() => setMode('mutate')} />
              Mutate
            </label>
          </div>
        </div>

        {mode === 'mutate' && (
          <div className="form-group">
            <label className="form-label">Stages</label>
            <div className="checkbox-row">
              {ALL_STAGES.map((s) => (
                <label key={s} className={`chip ${s === 'create' ? 'chip-warn' : ''}`}>
                  <input type="checkbox" checked={stages.includes(s)} onChange={() => toggleStage(s)} />
                  {s}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Safety</label>
          <div className="checkbox-row">
            <label className="chip">
              <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />
              Dry-run (no changes pushed)
            </label>
            <label className="chip" title="Bypass snapshot TTL (force re-fetch from BC)">
              <input type="checkbox" checked={forceFresh} onChange={(e) => setForceFresh(e.target.checked)} />
              Force fresh snapshot
            </label>
          </div>
        </div>
      </div>

      {showCreateWarning && (
        <div className="sync-all-warning">
          <strong>create stage selected.</strong> New BC products will be POSTed with default
          weight=0, price=0, is_visible=false. Akeneo writeback runs inline after each create.
          Only enable this if you've reviewed which SKUs are missing in BC.
        </div>
      )}
      {showRealMutateWarning && (
        <div className="sync-all-warning danger">
          <strong>Dry-run is OFF.</strong> This will push real changes to BigCommerce for{' '}
          <strong>every eligible product</strong> in the selected market(s). Review compare
          results in Diff Explorer first if you haven't already.
        </div>
      )}

      <div className="sync-all-actions">
        {validationError && <span className="muted error-inline">{validationError}</span>}
        <button
          type="button"
          className="primary"
          disabled={submitting || Boolean(validationError)}
          onClick={onSubmit}
        >
          {submitting
            ? 'Enqueueing…'
            : mode === 'compare'
              ? `Enqueue compare for ${markets.length || 0} market(s)`
              : dryRun
                ? `Enqueue dry-run mutate for ${markets.length || 0} market(s)`
                : `Enqueue REAL mutate for ${markets.length || 0} market(s)`}
        </button>
      </div>

      {results.length > 0 && (
        <table className="sync-all-results">
          <thead>
            <tr><th>Market</th><th>Status</th><th>Job</th><th>Detail</th></tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.market}>
                <td>{r.market}</td>
                <td>
                  <span className={`status status-${r.status}`}>{r.status}</span>
                </td>
                <td>
                  {r.jobId
                    ? <Link to={`/jobs/history/${r.jobId}`}>Job {r.jobId}</Link>
                    : '—'}
                </td>
                <td className="muted">{r.error || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default SyncAllMarketsCard;
